/**
 *
 *  User Profile Page
 *  SessionDetails Component
 *
 *  Created by Vladimir Shishlyannikov on 06/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useContext,
} from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import Transition, {
  ENTERING,
  ENTERED,
  EXITING,
  EXITED,
} from "react-transition-group/Transition";
import Modal from "react-modal";
import moment from "moment-timezone"; // TODO: use dayjs instead
import { FormattedMessage } from "react-intl";

import { useAppState } from "../../../state";
import { useRegion } from "../../../context/RegionProvider";
import { ChatContext } from "../../../context/ChatProvider"; // Adjust the import path as necessary

// Components
import Button from "../../Button/Button";
import TBKTooltip from "../../TruBeTooltip/TruBeTooltip";
import { WorkoutIcon } from "../../PartnerProductBadge/PartnerProductBadge";
import GoogleMaps from "../../LocationPicker/GoogleMaps";
import GoogleMap from "../../LocationPicker/GoogleMap/GoogleMap";
import Marker from "../../LocationPicker/Marker/Marker";
import ScrollToTopOnMount from "../../ScrollToTopOnMount/ScrollToTopOnMount";
import RepeatBookingFlowPopup from "../../RepeatBookingFlowPopup/RepeatBookingFlowPopup";
import BookingFlowPanelHeader from "../../BookingFlow/BookingFlowPanelHeader/BookingFlowPanelHeader";
import RepeatBookingFlowPanel from "../../RepeatBookingFlowPanel/RepeatBookingFlowPanel";
import TruBeDrawer from "../../TruBeDrawer/TruBeDrawer";
import TimeZoneLabel from "../../TimeZoneLabel/TimeZoneLabel";

// Hooks
import useTheme from "../../ThemeProvider/useTheme/useTheme";

// Icons
import MapMarkerIcon from "../../../icons/MapMarkerIcon";
import CalendarTruBeIcon from "../../../icons/CalendarTruBeIcon";
import CopyIcon from "../../../icons/CopyIcon";
import MobilePhoneIcon from "../../../icons/MobilePhoneIcon";
import CloseIcon from "../../../icons/CloseIcon";
// import RateSession from "../RateSession/RateSession";
// import CaretDownIcon from "../../../icons/CaretDownIcon";
import HeavyPlusIcon from "../../../icons/HeavyPlusIcon";
import SpeechBalloonIcon from "../../../icons/SpeechBalloonIcon";
import CheckNiceIcon from "../../../icons/CheckNiceIcon";
import BlockerIcon from "../../../icons/BlockerIcon";
import CaretDownIcon from "../../../icons/CaretDownIcon";
import MessagesIcon from "../../../icons/MessagesIcon";

// Utils
import { getTz } from "../../../utils/date";
import copy from "../../../utils/copy";
import {
  clientWidth,
  getPartnerPictureUrl,
  makeCancelable,
  toQueryString,
} from "../../../utils";
import { modalStyles } from "../../../constants";
import ics from "../../../lib/icsFormatter/icsFormatter";

// Images
import defaultPartnerPhoto from "../../../images/generic-trainerphoto.svg";

const transitionClasses = {
  [ENTERED]: "booking-flow-popup-transition-entered",
  [ENTERING]: "booking-flow-popup-transition-entering",
  [EXITING]: "booking-flow-popup-transition-exiting",
  [EXITED]: "booking-flow-popup-transition-exited",
};

const AUTOHIDE_TIME = 3000;

const ReadMore = ({ text }) => {
  const { theme } = useTheme();

  const [showMore, setShowMore] = useState(
    false
    //clientWidth() >= theme.responsive.breakpoints.lg
  );

  const maxText = clientWidth() >= theme.responsive.breakpoints.lg ? 128 : 128;
  const height = clientWidth() >= theme.responsive.breakpoints.lg ? 96 : 96;

  let lessText = text.slice(0, maxText);

  const handleReadMoreClick = useCallback(() => {
    setShowMore(true);
  }, []);

  return (
    <div className="tbk-relative">
      <div className="tbk-overflow-hidden">
        <p className="tbk-text-main tbk-mb-2 tbk-mb-2 tbk-text-primary">
          {!showMore && text.length > maxText ? `${lessText} ...` : text}
        </p>
      </div>
      {!showMore && text.length > maxText && (
        <div className="tbk-text-right">
          <button
            className="tbk-text-main tbk-inline-flex tbk-text-coral tbk-underline"
            onClick={handleReadMoreClick}
          >
            Read More
            <CaretDownIcon />
          </button>
        </div>
      )}
    </div>
  );
};

const SessionDetails = () => {
  const history = useHistory();
  const { BookingId } = useParams();
  const { theme } = useTheme();
  const { region } = useRegion();
  const { chatClient, updateChannelMembershipsForDirects } =
    useContext(ChatContext);

  const {
    user,
    loadBooking,
    cancelBooking,
    deletePayment,
    loadPartnerAvailability,
    booking,
    customerPayment,
    searchPayment,
    partnerAvailability,
    partnerAvailabilityLoaded,
    updateCustomerInfo,
    sendSupportRequest,
    clearPartnerAvailabilityLoaded,
    error,
    isFetching,
  } = useAppState();

  const userMedicalInformation = user && user.medicalInformation;

  const medicalInformationRef = useRef();
  const panelRef = useRef(null);
  const repeatBookingFlowPopupRef = useRef(null);
  const repeatBookingFlowPanelRef = useRef(null);

  const defaultCustomStyle =
    clientWidth() < theme.responsive.breakpoints.sm
      ? Object.assign(
          {},
          { ...modalStyles.mobileDefault },
          {
            overlay: {
              ...modalStyles.mobileDefault.overlay,
              zIndex: 1060,
            },
          }
        )
      : Object.assign(
          {},
          { ...modalStyles.default },
          { overlay: { ...modalStyles.default.overlay, zIndex: 1060 } }
        );

  // eslint-disable-next-line
  const [bookingId, setBookingId] = useState(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [medicalNotes, setMedicalNotes] = useState(userMedicalInformation);
  const [isMedicalNotesModalOpen, setIsMedicalNotesModalOpen] = useState(false);
  const [isCopyTooltipShow, setIsCopyTooltipShow] = useState(false);
  const [isContactSupportModalOpen, setIsContactSupportModalOpen] =
    useState(false);
  const [customerEmail, setCustomerEmail] = useState(user && user.email);
  const [customerRequestMessage, setCustomerRequestMessage] = useState("");
  const [hasEmailError, setHasEmailError] = useState(false);
  const [isCancelSessionModalOpen, setIsCancelSessionModalOpen] =
    useState(false);
  const [hasBeenCancelled, setHasBeenCancelled] = useState(false);
  const [isContactTrainerModalOpen, setIsContactTrainerModalOpen] =
    useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalTitle, setInfoModalTitle] = useState("");
  const [infoModalMsg, setInfoModalMsg] = useState("");

  const [nowTime] = useState(moment(new Date()).tz(getTz(region)));
  const [availabilityCalendarMin] = useState(
    moment(new Date())
      .tz(getTz(region))
      .startOf("day")
      .add(330 /*05:30*/, "minute")
  );
  const [availabilityCalendarMax] = useState(
    moment(new Date())
      .tz(getTz(region))
      .startOf("day")
      .add(1410 /*23:30*/, "minute")
      .add(3, "month")
  );
  const [_directBookingTime, setDirectBookingTime] = useState(undefined);
  const [isRepeatBookingFlowPopupOpen, setIsRepeatBookingFlowPopupOpen] =
    useState(false);
  const [isRepeatBookingFlowPanelOpen, setIsRepeatBookingFlowPanelOpen] =
    useState(false);
  const [
    isRepeatBookingFlowPanelFullyOpen,
    setIsRepeatBookingFlowPanelFullyOpen,
  ] = useState(false);

  const mapRef = React.useRef();
  const mapElementRef = React.useRef(null);
  const copyButtonRef = React.useRef(null);
  const hideCancelablePromiseRef = React.useRef(null);
  const customerEmailRef = useRef();
  const customerRequestMessageRef = useRef();
  const infoModalResolveRef = useRef(null);

  useEffect(() => {
    if (user && user.id && BookingId) {
      setBookingId(BookingId);
      loadBooking(user.id, Number(BookingId));

      searchPayment(user.id, {
        type: "BOOKING",
        attributes: {
          bookingId: Number(BookingId),
        },
      }).catch((err) => {});
    } else {
      setBookingId(null);
    }
  }, [user, BookingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openContactSupportModal = useCallback(() => {
    setIsContactSupportModalOpen(true);
  }, []);

  const closeContactSupportModal = useCallback(() => {
    setIsContactSupportModalOpen(false);
  }, []);

  const openMedicalNotesModal = useCallback(() => {
    setIsMedicalNotesModalOpen(true);
  }, []);

  const closeMedicalNotesModal = useCallback(() => {
    setIsMedicalNotesModalOpen(false);
  }, []);

  const openCancelSessionModal = useCallback(() => {
    setIsCancelSessionModalOpen(true);
  }, []);

  const closeCancelSessionModal = useCallback(() => {
    setIsCancelSessionModalOpen(false);
  }, []);

  const openContactTrainerModal = useCallback(() => {
    setIsContactTrainerModalOpen(true);
  }, []);

  const closeContactTrainerModal = useCallback(() => {
    setIsContactTrainerModalOpen(false);
  }, []);

  const createDirectConversation = useCallback(async () => {
    history.push(`/account/messages/partner_${booking.partner.id}`);

    return;
    if (!chatClient) {
      console.error("Chat client is not initialized");
      return;
    }

    try {
      // Create a new direct conversation
      const channel = await chatClient.createDirectConversation({
        userId: `partner_${booking.partner.id}`, // The ID of the user you want to start a conversation with
      });

      // Optionally, update the memberships to reflect the new conversation
      await updateChannelMembershipsForDirects(chatClient);

      console.log("Direct conversation created:", channel);
      history.push(`/account/messages/partner_${booking.partner.id}`);
    } catch (error) {
      console.error("Error creating direct conversation:", error);
    }
  }, [booking]);

  const openInfoModal = useCallback((msg, title = "Sorry") => {
    return new Promise((resolve, reject) => {
      infoModalResolveRef.current = resolve;

      setIsInfoModalOpen(true);
      setInfoModalTitle(title);
      setInfoModalMsg(msg);
    });
  }, []);

  const closeInfoModal = useCallback(() => {
    infoModalResolveRef.current();
    infoModalResolveRef.current = null;

    setIsInfoModalOpen(false);
  }, []);

  const handleMedicalNotesChange = useCallback((event) => {
    const value = event.target.value;

    setMedicalNotes(value);
  }, []);

  const handleAddToCalClick = useCallback(
    (e) => {
      const title = `Your ${booking.product.name} session with ${booking.partner.firstName}`;
      const place = `${booking.location.address} (${booking.location.detail})`;
      const begin = moment.utc(booking.start).local();
      const end = begin.clone().add(1, "h");

      const clientNotes = booking.note || "";

      ics.removeAllEvents();
      ics.addEvent(
        title,
        clientNotes,
        place,
        begin.toISOString(),
        end.toISOString()
      );

      ics.saveFile("Booking-Event");

      // if (isIosDevice()) {
      // @ts-ignore
      // } else {
      // ics?.download('calendar');
      // }
    },
    [booking]
  );

  const handleCopyAddressToClipboard = useCallback(
    (e) => {
      if (booking && copy(booking.location.address)) {
        showCopyTooltip();

        // Analytics.trackEvent(SHARE_CATEGORY, '', target.tagName.toLocaleLowerCase() === 'button' ? EVENT_SHARE_LINK : EVENT_SHARE_CLIPBOARD);
      }
    },
    [booking] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const showCopyTooltip = useCallback(() => {
    if (hideCancelablePromiseRef.current) {
      return;
    }

    // Auto hide afer 4 sec
    var hidePromise = new Promise((resolve, reject) => {
      setIsCopyTooltipShow(true);

      setTimeout(() => {
        resolve();
      }, AUTOHIDE_TIME);
    });

    hideCancelablePromiseRef.current = makeCancelable(hidePromise);

    return hideCancelablePromiseRef.current.promise.then(() => {
      hideCancelablePromiseRef.current = null;
      hideCopyTooltip();
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hideCopyTooltip = useCallback(() => {
    if (hideCancelablePromiseRef.current) {
      hideCancelablePromiseRef.current.cancel();
      hideCancelablePromiseRef.current = null;
    }

    setIsCopyTooltipShow(false);
  }, []);

  const handleGoogleMapsLoaded = useCallback((map) => {
    mapRef.current = map;

    setMapsLoaded(true);
  }, []);

  const handleOpenInMaps = useCallback(() => {
    if (!booking) {
      return;
    }

    window.open(
      `https://maps.google.com/?q=${booking.location.latitude},${booking.location.longitude}`,
      "_blank" // <- This is what makes it open in a new window.
    );
  }, [booking]);

  const handleMedicalNotesSubmit = useCallback(
    (e) => {
      const userId = user.id;
      let medicalInformation = medicalInformationRef.current.value;

      // if (
      //   medicalInformation &&
      //   userMedicalInformation !== medicalInformation &&
      //   medicalInformation.length > 5
      // ) {
      updateCustomerInfo({ medicalInformation }, userId);

      closeMedicalNotesModal();
      // }
    },
    [user, userMedicalInformation, updateCustomerInfo] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleCustomerEmailChange = useCallback((event) => {
    const value = event.target.value;

    setCustomerEmail(value);
    setHasEmailError(false);
  }, []);

  const handleCustomerRequestMessageChange = useCallback((event) => {
    const value = event.target.value;

    setCustomerRequestMessage(value);
  }, []);

  const handleCustomerRequestSubmit = useCallback(
    (e) => {
      // const userId = user.id;
      let customerMessage = customerRequestMessageRef.current.value;

      if (!user || !user.email) {
        setHasEmailError(true);

        return;
      }

      if (customerMessage && customerMessage.length > 1) {
        sendSupportRequest(
          `${user.firstName} ${user.lastName}`,
          user.email,
          "Booking support",
          `${customerMessage} <br /> BookingId: ${booking.id} <br /> ${user.firstName} ${user.lastName} <br /> ${user.email}`
        ).then(() => {
          setCustomerRequestMessage("");
          closeContactSupportModal();
          openInfoModal(
            "Thank you for the feedback, we will contact you soon.",
            "Your request has been sent"
          );
        });
      }
    },
    [user, userMedicalInformation, updateCustomerInfo, booking] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const sendBooking = useCallback(() => {
    var urlParams = {
      // postal_code: booking.location.code.replace("CA ", ""),
      location: `${booking.location.latitude},${booking.location.longitude}`,
      address: booking.location.address,
      partner: booking.partner.id,
      product: booking.product.id,
      start_time: _directBookingTime,
      detail: booking.location.detail,
    };

    if (booking.location.gymName) {
      urlParams.place = booking.location.code;

      if (booking.location.gymName) {
        urlParams.place_name = booking.location.gymName;
      }
    } else {
      urlParams.postal_code = booking.location.code.replace("CA ", "");
    }

    history.push(`/checkout?${toQueryString(urlParams)}`);
  }, [history, booking, _directBookingTime]);

  const handleDirectBookingTimeChange = useCallback(
    (dateTime, isTimeSelected) => {
      // Analytics.trackEvent(TRAINERS_LIST_PAGE_CATEGORY, '', EVENT_DATETIME_CHANGE);

      setDirectBookingTime(dateTime ? dateTime.getTime() : undefined);
    },
    []
  );

  const closeRepeatBookingFlowPopup = useCallback(() => {
    setIsRepeatBookingFlowPopupOpen(false);
  }, []); // eslint-disable-line

  const closeRepeatBookingFlowPanel = useCallback(() => {
    setIsRepeatBookingFlowPanelOpen(false);
    setIsRepeatBookingFlowPanelFullyOpen(false);
  }, []); // eslint-disable-line

  const handleBottomReached = useCallback(() => {
    if (isRepeatBookingFlowPanelOpen) {
      setIsRepeatBookingFlowPanelOpen(false);
    }
  }, [isRepeatBookingFlowPanelOpen]);

  const handleFullyOpen = useCallback(() => {
    setIsRepeatBookingFlowPanelFullyOpen(true);
  }, []);

  const handleRepeatBookingClick = useCallback(() => {
    if (booking) {
      loadPartnerAvailability(booking.partner.id); // Time slots
    }

    if (clientWidth() < theme.responsive.breakpoints.lg) {
      setIsRepeatBookingFlowPanelOpen(true);
    } else {
      setIsRepeatBookingFlowPopupOpen(true);
    }

    // Analytics.trackEvent(TRAINERS_LIST_PAGE_CATEGORY, 'Open', EVENT_TIME_SLOTS_POPUP_OPEN);
  }, [booking]);

  const handleRepeatBookingFlowCtaClick = useCallback(() => {
    sendBooking();
  }, [sendBooking]);

  const handleCancelSession = useCallback(() => {
    const userId = user.id;

    cancelBooking(customerPayment.id, customerPayment.customerId)
      .promise.then(() => {
        setHasBeenCancelled(true);
      })
      .catch((err) => {
        if (err) {
          openInfoModal(err.message);
        }
      });
  }, [user, customerPayment]);

  const isCancelled = booking ? booking.status === "CANCELLED" : "";

  return (
    <>
      <ScrollToTopOnMount />
      <div className="tbk--mx-2 tbk-mt-4 lg:tbk-mx-0 lg:tbk-mt-0 lg:tbk-items-start lg:tbk-gap-5 xl:tbk-grid xl:tbk-grid-cols-account-two-cols-layout">
        <div className="tbk-relative tbk-mb-5 tbk-overflow-hidden tbk-rounded-2xl tbk-bg-basic-white tbk-p-2 tbk-shadow-trube md:tbk-p-3">
          {isCancelled ? (
            <div className="tbk-text-small tbk-absolute tbk-left-0 tbk-top-0 tbk-rounded-lg tbk-bg-blue-grey tbk-px-1 tbk-py-0.5 tbk-text-basic-white lg:tbk-left-auto lg:tbk-right-1 lg:tbk-top-1">
              Cancelled
            </div>
          ) : null}
          <div className="tbk-mb-3 tbk-flex tbk-border-b tbk-border-solid tbk-border-blue-grey-light md:tbk-border-none">
            <div className="tb-profile-picture empty tbk-mr-2 tbk-h-28 tbk-shrink-0 tbk-grow-0 tbk-basis-28 md:tbk-mb-0.5 md:tbk-h-40 md:tbk-basis-40 lg:tbk-mr-3">
              <Link to={`/partners/${booking ? booking.partner.id : ""}`}>
                <img
                  className="tb-profile-picture-img img-responsive tbk-h-full tbk-w-full tbk-rounded-lg tbk-object-cover"
                  itemProp="image"
                  src={
                    booking
                      ? getPartnerPictureUrl(booking.partner)
                      : defaultPartnerPhoto
                  }
                  alt="Coach profile"
                />
              </Link>
            </div>
            <div className="tbk-min-w-0">
              <div className="tbk-mb-0.5 tbk-flex tbk-items-center md:tbk-mb-2">
                <div className="tbk-mr-0.5 tbk-hidden md:tbk-block">
                  <WorkoutIcon size={32} />
                </div>
                <div className="tbk-text-secondary md:tbk-text-h3-subtitle tbk-mr-1 tbk-whitespace-nowrap tbk-text-primary">
                  {booking && booking.product.name}
                </div>
                <div className="tbk-text-secondary md:tbk-text-main tbk-flex tbk-overflow-hidden tbk-whitespace-nowrap tbk-text-blue-grey">
                  <div className="tbk-hidden tbk-max-w-full tbk-overflow-hidden tbk-text-ellipsis tbk-align-middle md:tbk-inline-block">
                    session&nbsp;
                  </div>
                  <dspan className="tbk-max-w-full tbk-overflow-hidden tbk-text-ellipsis tbk-align-middle">
                    with
                  </dspan>
                </div>
              </div>
              <div className="tbk-text-h3-subtitle md:tbk-text-bright tbk-mb-0.5 tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap md:tbk-mb-2">
                <Link
                  to={`/partners/${booking ? booking.partner.id : ""}`}
                  className="tbk-text-primary"
                >
                  {booking && booking.partner.firstName}{" "}
                  {booking && booking.partner.lastName}
                </Link>
              </div>
              <div className="tbk-flex tbk-items-center">
                <div className="tbk-text-main tbk-mr-2 tbk-hidden tbk-text-blue-grey md:tbk-block">
                  Session type:
                </div>
                <div className="tbk-flex tbk-items-center">
                  <div className="tbk-mr-0.5 tbk-shrink-0">
                    <MapMarkerIcon size={32} />
                  </div>
                  <div className="">In-Person</div>
                </div>
              </div>
            </div>
          </div>
          {booking &&
          (booking.status === "COMPLETED" || booking.status === "CANCELLED") ? (
            <Button
              className="tbk-mb-5 tbk-w-full md:tbk-hidden"
              onClick={handleRepeatBookingClick}
            >
              Repeat booking
            </Button>
          ) : /*<Button
              className="tbk-mb-5 tbk-w-full md:tbk-hidden"
              startIcon={<MessagesIcon className="tbk-text-basic-white" />}
              onClick={createDirectConversation}
            >
              Contact <FormattedMessage id="Trainer" defaultMessage="Trainer" />
            </Button>*/ null}

          <div className="tbk-text-small tbk-mb-0.5 tbk-text-primary">
            Date and time:
          </div>
          <div className="tbk-mb-0.5 tbk-flex tbk-items-center tbk-rounded-lg tbk-border tbk-border-blue-grey-light tbk-bg-grey-light tbk-px-2 tbk-py-1">
            <div
              className={`tbk-mr-1 tbk-shrink-0 ${
                isCancelled ? "tbk-text-blue-grey" : "tbk-text-green"
              }`}
            >
              <CalendarTruBeIcon size={32} />
            </div>
            <div className="tbk-text-main tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-primary">
              {booking &&
                moment
                  .utc(booking.start)
                  .tz(getTz(region))
                  .format(
                    clientWidth() < theme.responsive.breakpoints.sm
                      ? "h:mma ddd, MMMM DD, YYYY"
                      : "h:mma dddd, MMMM DD, YYYY"
                  )}
            </div>
          </div>
          <div className="tbk-text-small tbk-mb-5 tbk-text-blue-grey">
            Time set to{" "}
            <TimeZoneLabel
              region={user ? user.region : process.env.REACT_APP_REGION}
            />
          </div>

          <div className="tbk-mb-2">
            <GoogleMaps
              mapElement={mapElementRef}
              onLoaded={handleGoogleMapsLoaded}
            >
              <div style={{ height: `${152}px` }}>
                <div ref={mapElementRef} className="tbk-h-full tbk-rounded-lg">
                  {mapsLoaded && booking ? (
                    <GoogleMap
                      defaultZoom={10}
                      defaultCenter={{
                        lat: booking.location.latitude,
                        lng: booking.location.longitude,
                      }}
                      defaultOptions={{
                        disableDefaultUI: true,
                        // styles: [{
                        //   featureType: 'all',
                        //   elementType: 'labels',
                        //   stylers: [{
                        //     visibility: 'off'
                        //   }]
                        // }]
                      }}
                    >
                      <Marker
                        position={{
                          lat: booking.location.latitude,
                          lng: booking.location.longitude,
                        }}
                      />
                    </GoogleMap>
                  ) : null}
                </div>
              </div>
            </GoogleMaps>
          </div>

          <div className="tbk-mb-4 tbk-flex tbk-flex-col tbk-justify-evenly md:tbk-flex-row">
            <div className="with-tooltip tbk-relative tbk-mb-2 tbk-grow md:tbk-mb-0 md:tbk-mr-2">
              <Button
                ref={copyButtonRef}
                variant="secondary"
                size={"md"}
                endIcon={<CopyIcon />}
                className="tbk-w-full"
                onClick={handleCopyAddressToClipboard}
              >
                Copy address
              </Button>
              <TBKTooltip
                element={copyButtonRef.current}
                adjust
                inside
                placement="top"
                isShow={isCopyTooltipShow}
                onClick={hideCopyTooltip}
              >
                Copied!
              </TBKTooltip>
            </div>

            <Button
              variant="secondary"
              size={"md"}
              className="tbk-grow"
              onClick={handleOpenInMaps}
            >
              Open in Maps
            </Button>
          </div>

          <div className="tbk-text-small tbk-mb-0.5 tbk-text-primary">
            Training location:
          </div>
          <div className="tbk-mb-2 tbk-flex tbk-items-center tbk-rounded-lg tbk-border tbk-border-blue-grey-light tbk-bg-grey-light tbk-px-2 tbk-py-1">
            <div className="tbk-mr-1 tbk-shrink-0 tbk-text-green">
              <MapMarkerIcon size={32} />
            </div>
            {booking && booking.location.gymName ? (
              <div className="tbk-text-main tbk-overflow-hidden_ tbk-text-ellipsis_ tbk-whitespace-nowrap tbk-text-primary">
                {booking.location.gymName} (
              </div>
            ) : null}
            <div className="tbk-text-main tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-primary">
              {booking && booking.location.address}
              {booking && booking.location.gymName ? ")" : ""}
            </div>
          </div>

          <div className="tbk-text-small tbk-mb-0.5 tbk-text-primary">
            Address details:
          </div>
          <div className="tbk-mb-2 tbk-flex tbk-items-center tbk-rounded-lg tbk-border tbk-border-blue-grey-light tbk-bg-grey-light tbk-px-2 tbk-py-1">
            <div
              className={`tbk-text-main tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap ${
                booking && booking.location.detail
                  ? "tbk-text-primary"
                  : " tbk-text-grey-inactive"
              }`}
            >
              {booking && booking.location.detail
                ? booking.location.detail
                : "None"}
            </div>
          </div>

          <div className="tbk-text-small tbk-mb-0.5 tbk-text-primary">
            Additional notes:
          </div>
          <div className="tbk-flex tbk-items-center tbk-rounded-lg tbk-border tbk-border-blue-grey-light tbk-bg-grey-light tbk-px-2 tbk-py-1">
            <div
              className={`tbk-text-main ${
                booking && booking.note
                  ? "tbk-text-primary"
                  : " tbk-text-grey-inactive"
              }`}
            >
              {booking && booking.note ? booking.note : "None"}
            </div>
          </div>
        </div>

        <div className="tbk-mx-2 sm:tbk-mx-0">
          <div className="tbk-mb-2 tbk-hidden tbk-rounded-lg tbk-border-2 tbk-border-solid tbk-border-blue-grey-light tbk-p-3 md:tbk-block">
            <div className="tbk-text-h3-subtitle tbk-text-primary">
              Booking ID:&nbsp;&nbsp;&nbsp;
              <span className="tbk-text-main">{booking && booking.id}</span>
            </div>
          </div>
          {booking &&
          (booking.status === "COMPLETED" || booking.status === "CANCELLED") ? (
            <Button
              className="tbk-mb-2 tbk-hidden tbk-w-full md:tbk-block"
              onClick={handleRepeatBookingClick}
            >
              Repeat booking
            </Button>
          ) : /*<Button
              className="tbk-mb-2 tbk-hidden tbk-w-full md:tbk-block"
              startIcon={<MessagesIcon className="tbk-text-basic-white" />}
              onClick={createDirectConversation}
            >
              Contact <FormattedMessage id="Trainer" defaultMessage="Trainer" />
            </Button>*/ null}
          {booking &&
          booking.status !== "COMPLETED" &&
          booking.status !== "CANCELLED" ? (
            <Button
              variant="secondary"
              className="tbk-mb-2 tbk-w-full"
              startIcon={
                <span className="tbk-text-green">
                  <CalendarTruBeIcon />
                </span>
              }
              onClick={handleAddToCalClick}
            >
              Add to Calendar
            </Button>
          ) : null}
          <Button
            variant="secondary"
            className="tbk-mb-2 tbk-w-full"
            startIcon={
              <span className="tbk-text-primary">
                <SpeechBalloonIcon />
              </span>
            }
            onClick={() => setIsContactSupportModalOpen(true)}
          >
            Contact support
          </Button>
          {booking &&
          booking.status !== "COMPLETED" &&
          booking.status !== "CANCELLED" ? (
            <div className="tbk-relative">
              <Button
                variant="secondary"
                className="tbk-mb-2 tbk-w-full"
                startIcon={
                  <span className="tbk-text-coral">
                    <HeavyPlusIcon />
                  </span>
                }
                onClick={openMedicalNotesModal}
              >
                Medical notes
              </Button>

              <div
                className={`tbk-text-small tbk-absolute tbk-right-1 tbk-top-1 tbk-rounded-lg ${
                  medicalNotes ? "tbk-bg-blue" : "tbk-bg-green"
                } tbk-p-0.5 tbk-text-basic-white`}
              >
                {medicalNotes ? "Added" : "None"}
              </div>
            </div>
          ) : null}
          {customerPayment &&
          booking.status !== "COMPLETED" &&
          booking.status !== "CANCELLED" ? (
            <Button
              variant="secondary"
              className="tbk-mb-5 tbk-w-full"
              startIcon={<CloseIcon className="tbk-text-blue-grey" />}
              onClick={() => openCancelSessionModal()}
            >
              Cancel session
            </Button>
          ) : null}

          {/*<RateSession />*/}

          {booking && booking.partnerReview ? (
            <div className="tbk-mt-5">
              <div className="tbk-text-h3-subtitle tbk-mb-2 tbk-text-primary">
                How <FormattedMessage id="trainer" defaultMessage="trainer" />{" "}
                reviewed the session
              </div>
              <ReadMore text={booking.partnerReview} />
            </div>
          ) : null}
        </div>
      </div>
      <TruBeDrawer
        attachRef={panelRef}
        allowDragging={true}
        isOpen={isRepeatBookingFlowPanelOpen}
        containerStyle={{
          width: "100%",
          height: "100%",
          overflowY: "auto",
        }}
        title={BookingFlowPanelHeader}
        titleElementProps={{
          isOnline: false,
          partnerSummary: booking && booking.partner,
          partnerLoaded: booking && booking.partner,
          onClose: closeRepeatBookingFlowPanel,
        }}
        onFullyOpen={handleFullyOpen}
        onBottomReached={handleBottomReached}
        onClose={closeRepeatBookingFlowPanel}
      >
        {isRepeatBookingFlowPanelOpen &&
          clientWidth() < theme.responsive.breakpoints.lg && (
            <RepeatBookingFlowPanel
              ref={repeatBookingFlowPanelRef}
              className=""
              isFullyOpen={isRepeatBookingFlowPanelFullyOpen}
              region={user ? user.region : process.env.REACT_APP_REGION}
              min={availabilityCalendarMin.toDate()}
              max={availabilityCalendarMax.toDate()}
              bookingTime={_directBookingTime}
              partnerSummary={booking && booking.partner}
              partnerLoaded={booking && booking.partner}
              partnerAvailability={partnerAvailability}
              partnerAvailabilityLoaded={partnerAvailabilityLoaded}
              clearPartnerAvailabilityLoaded={clearPartnerAvailabilityLoaded}
              onDateTimeSelect={handleDirectBookingTimeChange}
              onClose={closeRepeatBookingFlowPanel}
              onCtaClick={handleRepeatBookingFlowCtaClick}
            />
          )}
      </TruBeDrawer>
      <Transition
        nodeRef={repeatBookingFlowPopupRef}
        in={
          clientWidth() >= theme.responsive.breakpoints.lg &&
          isRepeatBookingFlowPopupOpen
        }
        timeout={300}
        // addEndListener={this.handleTransitionEnd}
      >
        {(status, innerProps) => (
          <RepeatBookingFlowPopup
            //{...innerProps}
            ref={repeatBookingFlowPopupRef}
            className={`tbk-transition-transform tbk-duration-300 ${
              status === ENTERING || status === EXITED
                ? "tbk-translate-x-full tbk-opacity-5"
                : status === EXITING
                ? "tbk-translate-x-full tbk-opacity-5"
                : "tbk-translate-x-0 tbk-opacity-100"
            } ${transitionClasses[status]}`}
            region={user ? user.region : process.env.REACT_APP_REGION}
            sessionType={"OFFLINE"}
            userId={user && user.id}
            min={availabilityCalendarMin.toDate()}
            max={availabilityCalendarMax.toDate()}
            bookingTime={_directBookingTime}
            partnerSummary={booking && booking.partner}
            partnerLoaded={booking && booking.partner}
            partnerAvailability={partnerAvailability}
            partnerAvailabilityLoaded={partnerAvailabilityLoaded}
            clearPartnerAvailabilityLoaded={clearPartnerAvailabilityLoaded}
            onDateTimeSelect={handleDirectBookingTimeChange}
            onClose={closeRepeatBookingFlowPopup}
            onCtaClick={handleRepeatBookingFlowCtaClick}
          />
        )}
      </Transition>
      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={isContactSupportModalOpen}
        onRequestClose={closeContactSupportModal}
        style={defaultCustomStyle}
        contentLabel="Contact support"
      >
        <div className="modal-contact-support tbk-rounded-2xl tbk-bg-basic-white tbk-p-3 md:tbk-p-0">
          <button
            onClick={closeContactSupportModal}
            className="tbk-absolute tbk-right-2 tbk-top-2 tbk-z-10 tbk-text-coral"
          >
            <CloseIcon />
          </button>
          <h4 className="tbk-text-title-bold tbk-mb-0.5 tbk-mt-0 tbk-text-primary">
            Contact support
          </h4>
          <p className="tbk-text-main 3 tbk-mb-4 tbk-text-primary">
            Please, make sure your email address is correct, we will use it for
            the respond.
          </p>
          <div className="modal-body">
            <div className="tbk-text-small tbk-mb-0.5 tbk-text-primary">
              Your request:
            </div>
            <textarea
              ref={customerRequestMessageRef}
              className="tbk-input-field tbk-mb-3 tbk-w-full tbk-pb-3 tbk-pl-2 tbk-pr-2 tbk-pt-1.5 tbk-text-primary"
              name="customerMessage"
              rows="5"
              placeholder="Describe your issue"
              value={customerRequestMessage}
              onChange={handleCustomerRequestMessageChange}
            />
          </div>
          <div className="modal-footer">
            <Button
              className="tbk-w-full"
              disabled={!customerEmail || !customerRequestMessage || isFetching}
              showLoader={isFetching}
              onClick={handleCustomerRequestSubmit}
            >
              Send
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={isMedicalNotesModalOpen}
        onRequestClose={closeMedicalNotesModal}
        style={defaultCustomStyle}
        contentLabel="Medical notes"
      >
        <div className="modal-medical-notes tbk-rounded-2xl tbk-bg-basic-white tbk-p-3 md:tbk-p-0">
          <button
            onClick={closeMedicalNotesModal}
            className="tbk-absolute tbk-right-2 tbk-top-2 tbk-z-10 tbk-text-coral"
          >
            <CloseIcon />
          </button>
          <h4 className="tbk-text-title-bold tbk-mb-0.5 tbk-mt-0 tbk-text-primary">
            Medical information:
          </h4>
          <p className="tbk-text-main tbk-mb-3 tbk-text-primary">
            Add information about your medical condition or injures, so our
            <FormattedMessage id="trainers" defaultMessage="trainers" /> may
            build a correct workout plan for you.
          </p>
          <div className="modal-body">
            <div className="tbk-text-small tbk-mb-0.5 tbk-text-primary">
              Your medical information:
            </div>
            <textarea
              ref={medicalInformationRef}
              className="tbk-input-field tbk-mb-3 tbk-w-full tbk-pb-3 tbk-pl-2 tbk-pr-2 tbk-pt-1.5 tbk-text-primary"
              name="medicalNotes"
              rows="5"
              placeholder="Condition, injures, etc"
              value={medicalNotes}
              onChange={handleMedicalNotesChange}
            />
          </div>
          <div className="modal-footer">
            <Button
              className="tbk-w-full"
              // disabled={!medicalNotes || medicalNotes.length <= 5}
              endIcon={<CheckNiceIcon />}
              onClick={handleMedicalNotesSubmit}
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={isCancelSessionModalOpen}
        onRequestClose={closeCancelSessionModal}
        style={defaultCustomStyle}
        contentLabel="Cancel session"
      >
        <div className="modal-cancel-session tbk-rounded-2xl tbk-bg-basic-white tbk-p-3 md:tbk-p-0">
          {!hasBeenCancelled ? (
            <button
              onClick={closeCancelSessionModal}
              className="tbk-absolute tbk-right-2 tbk-top-2 tbk-z-10 tbk-text-coral"
            >
              <CloseIcon />
            </button>
          ) : null}
          <h4
            className={`tbk-text-title-bold tbk-mb-1 tbk-mt-0 tbk-text-grey-main${
              hasBeenCancelled ? " tbk-text-center" : ""
            }`}
          >
            {hasBeenCancelled
              ? "Your session has been cancelled"
              : "Cancel session"}
          </h4>
          {hasBeenCancelled ? (
            <>
              <p className="tbk-text-main tbk-mb-4 tbk-text-center tbk-text-grey-main">
                We'll notify the{" "}
                <FormattedMessage id="trainer" defaultMessage="trainer" /> about
                the cancellation.
              </p>
              <CheckNiceIcon
                size={64}
                className="tbk-mx-auto tbk-mb-4 tbk-text-green"
              />
            </>
          ) : (
            <>
              <p className="tbk-text-main tbk-mb-3 tbk-text-grey-main">
                Are you sure you want to cancel your session with{" "}
                {booking && booking.partner ? booking.partner.firstName : ""}{" "}
                {booking && booking.partner ? booking.partner.lastName : ""} on{" "}
                {booking &&
                  moment(moment.utc(booking.start))
                    .tz(getTz(region))
                    // .local()
                    .format("dddd, MMMM DD, YYYY [at] h:mma")}
                ?
              </p>
              {booking &&
              moment(moment.utc(booking.start))
                .tz(getTz(region))
                .diff(nowTime, "hours", true) > 12 ? (
                <p className="tbk-text-main tbk-mb-4 tbk-text-grey-main">
                  {booking.packageId
                    ? ""
                    : "Your payment will be refunded back in the next 3-5 working days."}
                </p>
              ) : (
                <p className="tbk-text-main tbk-mb-4 tbk-text-grey-main">
                  We cannot refund your session as it's less than 12 hours
                  before the scheduled start time. Please review our{" "}
                  <a href="/terms/Terms of Use and Booking.pdf" target="_blank">
                    Terms & Condition
                  </a>
                </p>
              )}
            </>
          )}
          {hasBeenCancelled ? (
            <Button className="tbk-w-full" onClick={closeCancelSessionModal}>
              Ok
            </Button>
          ) : (
            <>
              <Button
                color="coral"
                disabled={isFetching}
                className="tbk-mb-2 tbk-w-full"
                onClick={closeCancelSessionModal}
              >
                No, go back
              </Button>
              <Button
                variant="secondary"
                disabled={isFetching}
                showLoader={isFetching}
                className="tbk-mb-5 tbk-w-full"
                onClick={handleCancelSession}
              >
                Yes, Cancel the session
              </Button>
            </>
          )}
        </div>
      </Modal>
      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={isContactTrainerModalOpen}
        // onAfterOpen={afterOpenContactTrainerModal}
        onRequestClose={closeContactTrainerModal}
        style={defaultCustomStyle}
        contentLabel="Contact Coach Modal"
      >
        <div className="modal-info tbk-flex tbk-flex-col tbk-p-3 md:tbk-p-0">
          <button
            onClick={closeContactTrainerModal}
            className="tbk-absolute tbk-right-3 tbk-top-3 tbk-z-10 tbk-text-coral"
          >
            <CloseIcon />
          </button>
          <h3 className="tbk-text-title-bold tbk-mb-4 tbk-mt-0 tbk-text-grey-main">
            Contact <FormattedMessage id="Trainer" defaultMessage="Trainer" />
          </h3>
          <div className="tbk-mb-4 tbk-grow">
            <div className="tbk-flex tbk-rounded-lg tbk-bg-grey-light tbk-py-1.5">
              <MobilePhoneIcon
                size={64}
                className="tbk-mr-2 tbk-shrink-0 tbk-text-grey-main"
              />
              <div>
                <div className="tbk-text-secondary tbk-mb-0.5 tbk-text-grey-main">
                  Mobile:
                </div>
                <a
                  href={`tel:${booking && booking.phoneNumber}`}
                  className="tbk-text-title-medium tbk-mb-0.5 tbk-text-grey-main"
                >
                  {booking && booking.phoneNumber}
                </a>
              </div>
            </div>
          </div>
          <Button
            variant="secondary"
            className="tbk-mb-5 tbk-w-full"
            onClick={closeContactTrainerModal}
          >
            Close
          </Button>
        </div>
      </Modal>
      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={isInfoModalOpen}
        // onAfterOpen={afterOpenInfoModal}
        onRequestClose={closeInfoModal}
        style={
          clientWidth() < theme.responsive.breakpoints.sm
            ? modalStyles.infoMobile
            : modalStyles.info
        }
        contentLabel="Info Modal"
      >
        <div className="modal-info tbk-flex tbk-flex-col tbk-items-center">
          <button
            onClick={closeInfoModal}
            className="tbk-absolute tbk-right-2 tbk-top-2 tbk-z-10 tbk-hidden tbk-text-coral"
          >
            <CloseIcon />
          </button>
          <h3 className="tbk-text-title-bold tbk-mb-1 tbk-mt-0 tbk-text-center tbk-text-grey-main">
            {infoModalTitle}
          </h3>
          <p className="tbk-text-main tbk-mb-4 tbk-max-w-full tbk-text-center tbk-text-grey-main">
            {infoModalMsg}
          </p>
          {error ? (
            <BlockerIcon size={64} className="tbk-mb-4 tbk-text-green" />
          ) : (
            <CheckNiceIcon size={64} className="tbk-mb-4 tbk-text-green" />
          )}
          <Button className="tbk-w-full" onClick={closeInfoModal}>
            Ok
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default SessionDetails;
